<script setup lang="ts">
import { Badge, ProgressBar } from "@/design-system";
import type { Verdict } from "@/domain/verdict";

/**
 * Sticky readout of the current selection against the athlete's ceiling, so the
 * limit is visible *while* picking rather than after (FR-2.4).
 *
 * It bleeds past the screen gutter on purpose -- negative side margins plus a
 * translucent, blurred background, which is the one place the design system
 * allows blur ("only for translucency over scrolling content").
 */
defineProps<{ verdict: Verdict; eyebrow: string }>();
</script>

<template>
  <div
    class="meter"
    data-testid="live-meter"
  >
    <div class="meter__row">
      <div class="meter__figures">
        <p class="cw-eyebrow">
          {{ eyebrow }}
        </p>
        <p class="meter__value">
          <span data-testid="meter-value">{{ verdict.value }}</span>
          <span class="meter__unit">{{ verdict.unit }}</span>
          <span class="meter__of">{{ verdict.comparison }}</span>
        </p>
      </div>
      <Badge
        :tone="verdict.tone"
        data-testid="meter-badge"
      >
        {{ verdict.label }}
      </Badge>
    </div>

    <ProgressBar
      :value="verdict.fillPct"
      :max="100"
      :color="verdict.fillColor"
    >
      <template #marker>
        <span
          v-if="verdict.markerPct !== null"
          class="meter__marker"
          :style="{ left: `${verdict.markerPct}%` }"
          data-testid="meter-marker"
        />
      </template>
    </ProgressBar>
  </div>
</template>

<style scoped>
.meter {
  position: sticky;
  /* Not 0: with viewport-fit=cover the viewport top is behind the notch. */
  top: var(--safe-top);
  z-index: 5;
  /* Same bleed correction as .app-actions -- the shell's gutter now carries the
     safe-area insets. */
  margin: 0 calc((var(--gutter-screen) + var(--safe-right)) * -1) 0
    calc((var(--gutter-screen) + var(--safe-left)) * -1);
  padding: var(--space-5) calc(var(--gutter-screen) + var(--safe-right)) var(--space-6)
    calc(var(--gutter-screen) + var(--safe-left));
  background: color-mix(in oklab, var(--bg-page) 92%, transparent);
  backdrop-filter: var(--blur-sheet);
  border-bottom: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
.meter__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-5);
}
.meter__figures {
  min-width: 0;
}
.meter__figures .cw-eyebrow {
  margin: 0;
}
.meter__value {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 2px 0 0;
  font: var(--type-metric-sm);
  color: var(--text-strong);
  font-variant-numeric: tabular-nums;
}
.meter__unit {
  font: var(--type-unit);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}
.meter__of {
  font: var(--type-body-sm);
  color: var(--text-faint);
  white-space: nowrap;
}
.meter__marker {
  position: absolute;
  top: -3px;
  bottom: -3px;
  width: 2px;
  background: var(--border-strong);
  border-radius: 2px;
}
/* The bar needs to let the marker escape its rounded clip. */
.meter :deep(.cw-bar__track) {
  position: relative;
  overflow: visible;
}
.meter :deep(.cw-bar__fill) {
  border-radius: var(--radius-pill);
  height: 100%;
  transition: width var(--dur-slower, 520ms) var(--ease-out);
}
</style>
